import { useRef } from "react";
import { motion } from "framer-motion";

const chapters = [
    {
        n: "01",
        title: "Fragmented data ends here.",
        body: "One centralised, secure system replaces spreadsheets, inboxes and disconnected tools — a single source of truth across every worksite, department and the wider organisation.",
    },
    {
        n: "02",
        title: "Built for where work actually happens.",
        body: "Offshore, subsea, or in the field, Apex Pro captures cases with full offline functionality and syncs the moment connectivity returns. No signal, no problem, no lost data.",
    },
    {
        n: "03",
        title: "From report to resolution.",
        body: "Safety, Assurance and Audit cases flow through automated, rules-based workflows. Actions are assigned, tracked and closed out — with accountability built into every step.",
    },
    {
        n: "04",
        title: "Decisions backed by evidence.",
        body: "Benchmark safety performance across sites and teams. Real-time visibility and data-driven insight turn raw reporting into informed, defensible decisions.",
    },
];

const Chapter = ({ item, index }) => {
    const ref = useRef(null);
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.05 }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 border-t border-white/10"
        >
            <div className="md:col-span-3">
                <span className="font-display text-6xl md:text-7xl font-extrabold text-[#0052CC] leading-none">
                    {item.n}
                </span>
            </div>
            <div className="md:col-span-5">
                <h3 className="font-display text-2xl md:text-4xl font-bold text-white leading-[1.1] tracking-tight">
                    {item.title}
                </h3>
            </div>
            <div className="md:col-span-4 flex items-center">
                <p className="text-base md:text-lg text-white/65 leading-relaxed">
                    {item.body}
                </p>
            </div>
        </motion.div>
    );
};

export const Manifesto = () => {
    return (
        <section id="about" className="relative bg-[#091524] py-24 md:py-32 overflow-hidden">
            <div
                className="absolute inset-0 opacity-[0.18] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                <div className="max-w-3xl mb-8 md:mb-12">
                    <span className="label-eyebrow text-[#0052CC]">// The Manifesto</span>
                    <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.02]">
                        A safety platform for
                        <br />
                        <span className="text-white/40">high-risk industries.</span>
                    </h2>
                    <p className="mt-6 text-lg text-white/60 max-w-xl">
                        Designed by industry professionals, for industry professionals &mdash;
                        combining real-world operational knowledge with modern, intelligent
                        technology.
                    </p>
                </div>

                <div>
                    {chapters.map((c, i) => (
                        <Chapter key={c.n} item={c} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};
